import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCookie, faTimes } from '@fortawesome/free-solid-svg-icons';
import Cookies from 'js-cookie';
import './CookieBanner.css';

const COOKIE_CONSENT_KEY = 'centro-mattina-cookie-consent';

export type CookieConsent = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
};

const CookieBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Controlla se l'utente ha già fatto una scelta
    const consent = Cookies.get(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Mostra il banner dopo un piccolo delay per non essere invadente
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const saveConsent = (consent: CookieConsent) => {
    Cookies.set(COOKIE_CONSENT_KEY, JSON.stringify(consent), { expires: 365 });
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    });
  };

  const handleRejectNonEssential = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    });
  };

  const handleSavePreferences = () => {
    saveConsent({
      necessary: true,
      analytics: preferences.analytics,
      marketing: preferences.marketing,
      timestamp: Date.now(),
    });
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-banner-overlay">
      <div className="cookie-banner">
        <button 
          className="cookie-banner-close"
          onClick={handleRejectNonEssential}
          aria-label="Chiudi"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="cookie-banner-icon">
          <FontAwesomeIcon icon={faCookie} />
        </div>

        {!showSettings ? (
          <>
            <h3 className="cookie-banner-title">Utilizzo dei Cookie</h3>
            <p className="cookie-banner-text">
              Utilizziamo cookie e tecnologie simili per migliorare la tua esperienza di navigazione, 
              analizzare il traffico del sito e personalizzare i contenuti. Puoi scegliere quali 
              cookie accettare.
            </p>
            <p className="cookie-banner-text-small">
              Per maggiori informazioni consulta la nostra{' '}
              <Link to="/privacy-policy" className="cookie-banner-link">
                Privacy e Cookie Policy
              </Link>
              .
            </p>

            <div className="cookie-banner-buttons">
              <button
                className="cookie-btn cookie-btn-primary"
                onClick={handleAcceptAll}
              >
                Accetta tutti
              </button>
              <button
                className="cookie-btn cookie-btn-secondary"
                onClick={handleRejectNonEssential}
              >
                Solo necessari
              </button>
              <button
                className="cookie-btn cookie-btn-outline"
                onClick={() => setShowSettings(true)}
              >
                Personalizza
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="cookie-banner-title">Preferenze Cookie</h3>
            <p className="cookie-banner-text-small">
              Seleziona quali categorie di cookie desideri accettare. I cookie necessari sono 
              sempre attivi per garantire il corretto funzionamento del sito.
            </p>

            <div className="cookie-settings">
              <div className="cookie-setting-item">
                <div className="cookie-setting-header">
                  <label className="cookie-setting-label">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="cookie-checkbox"
                    />
                    <span className="cookie-setting-title">Cookie Necessari</span>
                  </label>
                  <span className="cookie-required">Sempre attivi</span>
                </div>
                <p className="cookie-setting-description">
                  Essenziali per il funzionamento del sito. Includono cookie di sessione 
                  e di sicurezza.
                </p>
              </div>

              <div className="cookie-setting-item">
                <div className="cookie-setting-header">
                  <label className="cookie-setting-label">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        setPreferences({ ...preferences, analytics: e.target.checked })
                      }
                      className="cookie-checkbox"
                    />
                    <span className="cookie-setting-title">Cookie Analitici</span>
                  </label>
                </div>
                <p className="cookie-setting-description">
                  Ci aiutano a capire come i visitatori interagiscono con il sito raccogliendo 
                  e analizzando informazioni in forma anonima.
                </p>
              </div>

              <div className="cookie-setting-item">
                <div className="cookie-setting-header">
                  <label className="cookie-setting-label">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) =>
                        setPreferences({ ...preferences, marketing: e.target.checked })
                      }
                      className="cookie-checkbox"
                    />
                    <span className="cookie-setting-title">Cookie di Marketing</span>
                  </label>
                </div>
                <p className="cookie-setting-description">
                  Utilizzati per tracciare i visitatori attraverso i siti web e mostrare 
                  annunci pertinenti e coinvolgenti.
                </p>
              </div>
            </div>

            <div className="cookie-banner-buttons">
              <button
                className="cookie-btn cookie-btn-primary"
                onClick={handleSavePreferences}
              >
                Salva preferenze
              </button>
              <button
                className="cookie-btn cookie-btn-outline"
                onClick={() => setShowSettings(false)}
              >
                Indietro
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
